import { useState, useCallback, useEffect } from 'react';
import type { VipEntitlement } from '@/api/auth';
import { clearPendingPaymentContext } from '@/utils/paymentContext';

export interface GlobalUser {
  userId: string;
  nickname: string;
  avatar: string | null;
  /** @deprecated use accessToken/refreshToken for auth */
  phone?: string;
  email?: string;
  points: number;
  isVip: boolean;
  vipExpireTime: string | null;
  vipEntitlement?: VipEntitlement | null;
  membershipStateVersion?: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** Local-only generate counter, not persisted to server */
  generateCount?: number;
}

// Keep UserState as an alias for backward compatibility
export type UserState = GlobalUser;

const DEFAULT_AVATAR = '/default-avatar.svg';
const AVATAR_OSS_PROCESS = 'x-oss-process=image/resize,m_fill,w_128,h_128/format,webp/quality,q_85';
const OSS_IMAGE_HOSTS = ['cdn.sparkdraw.cn', 'img.sparkdraw.cn', 'aliyuncs.com'];

let globalUser: GlobalUser | null = null;
let globalSetUserCallbacks: Array<(user: GlobalUser | null) => void> = [];

// True once bootstrapUser() has finished (success or failure).
// Prevents profile page from flashing the logged-out state on pull-to-refresh.
let globalAuthInitialized = !localStorage.getItem('gp_access_token');
let globalAuthCallbacks: Array<(v: boolean) => void> = [];

export const setAuthInitialized = () => {
  if (globalAuthInitialized) return;
  globalAuthInitialized = true;
  globalAuthCallbacks.forEach((cb) => cb(true));
};

const notifyAll = (user: GlobalUser | null) => {
  globalSetUserCallbacks.forEach((cb) => cb(user));
};

export interface UserInfo {
  userId: string;
  nickname: string;
  avatar?: string | null;
  phone?: string;
  email?: string;
  points?: number;
  isVip?: boolean;
  vipExpireTime?: string | null;
  vipEntitlement?: VipEntitlement | null;
}

/** Auth token persistence is owned by api/client; this store keeps only the UI projection. */
export function loginUser(
  user: UserInfo,
  tokens: { accessToken: string; refreshToken: string }
): void {
  globalUser = {
    userId: user.userId,
    nickname: user.nickname,
    avatar: user.avatar ?? null,
    phone: user.phone,
    email: user.email,
    points: user.points ?? 0,
    isVip: user.isVip ?? false,
    vipExpireTime: user.vipExpireTime ?? null,
    vipEntitlement: user.vipEntitlement ?? null,
    membershipStateVersion: user.vipEntitlement?.state_version ?? null,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
  };
  notifyAll(globalUser);
  setAuthInitialized();
}

export const logoutUser = () => {
  clearPendingPaymentContext();
  globalUser = null;
  notifyAll(null);
};

export const updateUser = (partial: Partial<GlobalUser>) => {
  if (globalUser) {
    globalUser = { ...globalUser, ...partial };
    notifyAll(globalUser);
  }
};

export const setPoints = (absoluteValue: number) => {
  if (globalUser) {
    globalUser = { ...globalUser, points: Math.max(0, absoluteValue) };
    notifyAll(globalUser);
  }
};

// Keep updatePoints for backward compatibility (delta-based)
// Second arg (_log) is accepted but ignored in v1 — v1 uses real API for points log
export const updatePoints = (delta: number, _log?: unknown) => {
  if (globalUser) {
    globalUser = { ...globalUser, points: Math.max(0, globalUser.points + delta) };
    notifyAll(globalUser);
  }
};

/** Increment local-only generate count (not persisted to server) */
export const incrementGenerateCount = () => {
  if (globalUser) {
    globalUser = { ...globalUser, generateCount: (globalUser.generateCount ?? 0) + 1 };
    notifyAll(globalUser);
  }
};

export const setVip = (vipExpireTime: string | null) => {
  if (globalUser) {
    globalUser = {
      ...globalUser,
      isVip: vipExpireTime !== null,
      vipExpireTime,
    };
    notifyAll(globalUser);
  }
};

export interface MembershipSnapshot {
  state_version: string;
  entitlement_active: boolean;
  expires_at: string;
  vip_entitlement: VipEntitlement | null;
}

export const applyMembershipSnapshot = (
  membership: MembershipSnapshot | null,
  pointsBalanceAfter: number,
): boolean => {
  if (!globalUser) return false;
  const currentVersion = globalUser.membershipStateVersion;
  if (membership) {
    if (currentVersion && membership.state_version <= currentVersion) return false;
    globalUser = {
      ...globalUser,
      points: Math.max(0, pointsBalanceAfter),
      isVip: membership.entitlement_active,
      vipExpireTime: membership.entitlement_active ? membership.expires_at : null,
      vipEntitlement: membership.vip_entitlement,
      membershipStateVersion: membership.state_version,
    };
  } else {
    // Once a versioned subscription chain has been observed, a null response can
    // only be an older concurrent read. Expiry and revocation remain versioned
    // snapshots of the same chain and therefore do not use null.
    if (currentVersion) return false;
    globalUser = {
      ...globalUser,
      points: Math.max(0, pointsBalanceAfter),
      isVip: false,
      vipExpireTime: null,
      vipEntitlement: null,
      membershipStateVersion: null,
    };
  }
  notifyAll(globalUser);
  return true;
};

export const updateAvatar = (avatarUrl: string) => {
  if (globalUser) {
    globalUser = { ...globalUser, avatar: avatarUrl };
    notifyAll(globalUser);
  }
};

function appendAvatarOssProcess(avatarUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(avatarUrl);
  } catch {
    return avatarUrl;
  }

  if (!OSS_IMAGE_HOSTS.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`))) {
    return avatarUrl;
  }

  const [urlWithoutHash, hash = ''] = avatarUrl.split('#');
  if (urlWithoutHash.includes('x-oss-process=')) {
    return avatarUrl;
  }
  const separator = urlWithoutHash.includes('?') ? '&' : '?';
  return `${urlWithoutHash}${separator}${AVATAR_OSS_PROCESS}${hash ? `#${hash}` : ''}`;
}

export function getAvatarUrl(user: GlobalUser | null): string {
  if (user && user.avatar) {
    return appendAvatarOssProcess(user.avatar);
  }
  return DEFAULT_AVATAR;
}

export const useAppStore = () => {
  const [user, setUser] = useState<GlobalUser | null>(globalUser);
  const [authInitialized, setAuthInitializedState] = useState(globalAuthInitialized);

  const register = useCallback((cb: (u: GlobalUser | null) => void) => {
    globalSetUserCallbacks.push(cb);
    return () => {
      globalSetUserCallbacks = globalSetUserCallbacks.filter((x) => x !== cb);
    };
  }, []);

  useEffect(() => {
    const unsubscribeUser = register(setUser);
    globalAuthCallbacks.push(setAuthInitializedState);
    return () => {
      unsubscribeUser();
      globalAuthCallbacks = globalAuthCallbacks.filter((x) => x !== setAuthInitializedState);
    };
  }, [register]);

  return { user, authInitialized };
};
